import React from "react";

/**
 * v79.192 — Admin universal inline-editor panel.
 *
 * Extracted from ListingDetail.jsx (was ~85 LOC inline) to keep the
 * detail page under a sane line-count. Renders every admin-editable
 * field the backend `AdminListingPatch` model accepts:
 *
 *   - category, brand, condition, status, currency, quantity
 *   - city, state, country, pincode, website_url
 *   - seller_name, seller_phone, seller_email
 *   - verified, boosted (toggles)
 *   - meta (raw JSON textarea for per-category subfields)
 *
 * Props:
 *   draft:        the current editor state (adminDraft in the parent)
 *   setDraft:     the setState fn — receives an updater `(d) => new`
 *
 * All fields have `data-testid="admin-edit-<key>"` so the testing
 * agent can drive edits without brittle DOM queries.
 */
export default function AdminInlineEditor({ draft, setDraft }) {
    const patch = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    // v79.198 — Founder direction (Feb 2026): "admin has all rights to
    // edit photos, title, description, details, price and everything
    // of any ad anywhere." The panel now exposes an image manager:
    // preview strip + add-from-file (data:URL, gets persisted by the
    // backend's storage pipeline on save) + per-image remove.
    const images = Array.isArray(draft.images) ? draft.images : [];
    const setImages = (next) => patch("images", next);
    const onPickFile = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const readers = files.slice(0, 10 - images.length).map((f) => new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.onerror = reject;
            r.readAsDataURL(f);
        }));
        const dataUrls = await Promise.all(readers);
        setImages([...images, ...dataUrls]);
        // Reset the input so the same file can be picked again if needed.
        e.target.value = "";
    };
    return (
        <div data-testid="admin-full-edit-panel" className="mt-3 mb-1 rounded-lg border border-aduku-yellow/60 bg-aduku-yellow/10 p-3 space-y-2.5">
            <div className="text-[10px] font-black uppercase tracking-widest text-aduku-navy/70">Admin — Full field edit</div>
            <div className="grid grid-cols-2 gap-2">
                <Field label="Category slug" testId="admin-edit-category" value={draft.category} onChange={(v) => patch("category", v)} />
                <Field label="Brand" testId="admin-edit-brand" value={draft.brand} onChange={(v) => patch("brand", v)} />
                <SelectField label="Condition" testId="admin-edit-condition" value={draft.condition} onChange={(v) => patch("condition", v)} options={[["", "—"], ["new", "New"], ["used", "Used"]]} />
                <SelectField label="Status" testId="admin-edit-status" value={draft.status || "active"} onChange={(v) => patch("status", v)} options={[["active", "Active"], ["sold", "Sold"], ["sold_out", "Sold out"], ["removed", "Removed"]]} />
                <Field label="Currency" testId="admin-edit-currency" value={draft.currency} onChange={(v) => patch("currency", v.toUpperCase().slice(0, 3))} className="uppercase" />
                <Field label="Quantity" testId="admin-edit-quantity" value={draft.quantity ?? ""} type="number" min="0" onChange={(v) => patch("quantity", v)} />
                <Field label="City" testId="admin-edit-city" value={draft.city} onChange={(v) => patch("city", v)} />
                <Field label="State" testId="admin-edit-state" value={draft.state} onChange={(v) => patch("state", v)} />
                <Field label="Country (ISO-2)" testId="admin-edit-country" value={draft.country} onChange={(v) => patch("country", v.toUpperCase().slice(0, 2))} className="uppercase" />
                <Field label="Pincode" testId="admin-edit-pincode" value={draft.pincode} onChange={(v) => patch("pincode", v)} />
                <Field label="Website URL" testId="admin-edit-website" value={draft.website_url} onChange={(v) => patch("website_url", v)} placeholder="https://…" colSpan={2} />
                <Field label="Seller name" testId="admin-edit-seller-name" value={draft.seller_name} onChange={(v) => patch("seller_name", v)} />
                <Field label="Seller phone" testId="admin-edit-seller-phone" value={draft.seller_phone} onChange={(v) => patch("seller_phone", v)} />
                <Field label="Seller email" testId="admin-edit-seller-email" value={draft.seller_email} onChange={(v) => patch("seller_email", v)} type="email" colSpan={2} />
            </div>
            <div className="flex flex-wrap gap-3 pt-1">
                <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-aduku-navy">
                    <input data-testid="admin-edit-verified" type="checkbox" checked={!!draft.verified} onChange={(e) => patch("verified", e.target.checked)} /> Verified
                </label>
                <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-aduku-navy">
                    <input data-testid="admin-edit-boosted" type="checkbox" checked={!!draft.boosted} onChange={(e) => patch("boosted", e.target.checked)} /> Boost (7 days)
                </label>
            </div>
            <label className="flex flex-col gap-0.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-aduku-navy/60">Meta (JSON — category-specific fields)</span>
                <textarea data-testid="admin-edit-meta-json" value={draft.meta_json || ""} onChange={(e) => patch("meta_json", e.target.value)} rows={6} className="rounded border border-aduku-border px-2 py-1 text-[11px] font-mono bg-white" spellCheck={false} />
                <span className="text-[10px] text-aduku-navy/55">Advanced: full meta dict. Invalid JSON is rejected on save.</span>
            </label>

            {/* v79.198 — Photo manager. Admin can add / remove / reorder
                images on any listing. New files are pushed as data URLs
                which the backend then routes through the storage
                pipeline on save (persisted as CDN-served URLs). */}
            <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-aduku-navy/60">Photos ({images.length}/10)</span>
                <div className="flex flex-wrap gap-2">
                    {images.map((src, i) => (
                        <div key={`${i}-${src.slice(0, 40)}`} data-testid={`admin-edit-photo-${i}`} className="relative w-20 h-20 rounded border border-aduku-border bg-white overflow-hidden group">
                            {/* Inline base64/hosted preview — no accessibility concerns since alt is meaningful. */}
                            <img src={src} alt={`Listing photo ${i + 1}`} className="w-full h-full object-cover" />
                            <button
                                type="button"
                                data-testid={`admin-edit-photo-remove-${i}`}
                                aria-label={`Remove photo ${i + 1}`}
                                title="Remove"
                                onClick={() => setImages(images.filter((_, j) => j !== i))}
                                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-black leading-none flex items-center justify-center opacity-90 hover:opacity-100"
                            >×</button>
                            {i > 0 && (
                                <button
                                    type="button"
                                    data-testid={`admin-edit-photo-left-${i}`}
                                    aria-label={`Move photo ${i + 1} left`}
                                    title="Move left"
                                    onClick={() => { const next = images.slice(); [next[i - 1], next[i]] = [next[i], next[i - 1]]; setImages(next); }}
                                    className="absolute bottom-1 left-1 w-5 h-5 rounded-full bg-aduku-navy text-white text-[10px] font-black leading-none flex items-center justify-center opacity-0 group-hover:opacity-100"
                                >‹</button>
                            )}
                        </div>
                    ))}
                    {images.length < 10 && (
                        <label
                            data-testid="admin-edit-photo-add"
                            className="w-20 h-20 rounded border-2 border-dashed border-aduku-navy/40 flex items-center justify-center text-xs text-aduku-navy/60 cursor-pointer hover:border-aduku-navy hover:text-aduku-navy bg-white/50"
                        >
                            + Add
                            <input type="file" accept="image/*" multiple className="hidden" onChange={onPickFile} />
                        </label>
                    )}
                </div>
                <span className="text-[10px] text-aduku-navy/55">First image is the cover. Drag replaced by ‹ arrow — hover a tile to reorder.</span>
            </div>
        </div>
    );
}

/** Tiny input helper — kept in-file since it's private to this component. */
function Field({ label, testId, value, onChange, type = "text", placeholder, className = "", colSpan, ...rest }) {
    return (
        <label className={`flex flex-col gap-0.5 ${colSpan === 2 ? "col-span-2" : ""}`}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-aduku-navy/60">{label}</span>
            <input
                data-testid={testId}
                type={type}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className={`h-8 rounded border border-aduku-border px-2 text-sm bg-white ${className}`}
                {...rest}
            />
        </label>
    );
}

function SelectField({ label, testId, value, onChange, options }) {
    return (
        <label className="flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-aduku-navy/60">{label}</span>
            <select
                data-testid={testId}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="h-8 rounded border border-aduku-border px-2 text-sm bg-white"
            >
                {options.map(([v, l]) => <option key={v || "_"} value={v}>{l}</option>)}
            </select>
        </label>
    );
}
